import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  PrinterIcon,
  CheckCircleIcon,
  CubeIcon,
  CurrencyDollarIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline'
import { consignmentService } from '../../services/consignmentService'
import toast from 'react-hot-toast'

export const ConsignmentDetailPage = () => {
  const { id } = useParams<{ id: string }>()

  // Modal states
  const [showRecallModal, setShowRecallModal] = useState(false)
  const [showSaleModal, setShowSaleModal] = useState(false)
  const [showReturnModal, setShowReturnModal] = useState(false)

  // Sale modal state
  const [selectedItem, setSelectedItem] = useState<any>(null)
  const [salePrice, setSalePrice] = useState('')
  const [saleSerialIds, setSaleSerialIds] = useState<Set<number>>(new Set())

  // Recall modal state
  const [recallItemIds, setRecallItemIds] = useState<Set<number>>(new Set())
  const [recallReason, setRecallReason] = useState('')

  // Return modal state
  const [returnSerialIds, setReturnSerialIds] = useState<Set<number>>(new Set())
  const [returnReason, setReturnReason] = useState('')

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['consignment', id],
    queryFn: () => consignmentService.getById(Number(id)),
  })

  const saleMutation = useMutation({
    mutationFn: (data: any) => consignmentService.recordSale(Number(id), data),
    onSuccess: () => {
      toast.success('Sale recorded successfully')
      closeSaleModal()
      refetch()
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to record sale')
    },
  })

  const recallMutation = useMutation({
    mutationFn: (data: any) => consignmentService.recall(Number(id), data),
    onSuccess: () => {
      toast.success('Items recalled successfully')
      closeRecallModal()
      refetch()
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to recall items')
    },
  })

  const returnMutation = useMutation({
    mutationFn: (data: any) => consignmentService.partialReturn(Number(id), data),
    onSuccess: () => {
      toast.success('Items returned successfully')
      closeReturnModal()
      refetch()
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to return items')
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  const consignment = data?.data?.data
  if (!consignment) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Consignment not found</p>
        <Link to="/consignments" className="text-primary-600 hover:underline mt-2 inline-block">
          Back to Consignments
        </Link>
      </div>
    )
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-blue-100 text-blue-800'
      case 'partially_sold': return 'bg-yellow-100 text-yellow-800'
      case 'fully_sold': return 'bg-green-100 text-green-800'
      case 'recalled': return 'bg-purple-100 text-purple-800'
      case 'closed': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const totalIssued = consignment.consignment_items?.reduce((sum: number, item: any) => sum + item.quantity_issued, 0) || 0
  const totalSold = consignment.consignment_items?.reduce((sum: number, item: any) => sum + item.quantity_sold, 0) || 0
  const totalReturned = consignment.consignment_items?.reduce((sum: number, item: any) => sum + item.quantity_returned, 0) || 0
  const totalRemaining = totalIssued - totalSold - totalReturned

  const progress = totalIssued > 0 ? Math.round((totalSold / totalIssued) * 100) : 0

  const isActive = ['active', 'partially_sold'].includes(consignment.status)

  // Helpers for modals
  const openSaleModal = (item: any) => {
    setSelectedItem(item)
    setSalePrice(item.unit_price?.toString() || '')
    setSaleSerialIds(new Set())
    setShowSaleModal(true)
  }

  const closeSaleModal = () => {
    setShowSaleModal(false)
    setSelectedItem(null)
    setSalePrice('')
    setSaleSerialIds(new Set())
  }

  const openRecallModal = () => {
    setRecallItemIds(new Set())
    setRecallReason('')
    setShowRecallModal(true)
  }

  const closeRecallModal = () => {
    setShowRecallModal(false)
    setRecallItemIds(new Set())
    setRecallReason('')
  }

  const openReturnModal = () => {
    setReturnSerialIds(new Set())
    setReturnReason('')
    setShowReturnModal(true)
  }

  const closeReturnModal = () => {
    setShowReturnModal(false)
    setReturnSerialIds(new Set())
    setReturnReason('')
  }

  const handleRecordSale = () => {
    if (saleSerialIds.size === 0) {
      toast.error('Please select at least one serial number')
      return
    }
    if (!salePrice || Number(salePrice) <= 0) {
      toast.error('Please enter a sale price')
      return
    }
    saleMutation.mutate({
      sales: [
        {
          item_id: selectedItem.item_id,
          quantity: saleSerialIds.size,
          serials: Array.from(saleSerialIds),
          price: Number(salePrice),
        },
      ],
    })
  }

  const handleRecall = () => {
    if (recallItemIds.size === 0) {
      toast.error('Please select at least one item to recall')
      return
    }
    recallMutation.mutate({
      item_ids: Array.from(recallItemIds),
      reason: recallReason || undefined,
    })
  }

  const handlePartialReturn = () => {
    if (returnSerialIds.size === 0) {
      toast.error('Please select at least one serial number to return')
      return
    }
    returnMutation.mutate({
      serials: Array.from(returnSerialIds),
      reason: returnReason || undefined,
    })
  }

  // Get issued serials for an item
  const getIssuedSerials = (item: any) => {
    return item.consignment_item_serials?.filter((s: any) => s.status === 'issued') || []
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/consignments" className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{consignment.consignment_number}</h1>
            <p className="text-sm text-gray-500">
              Issued {new Date(consignment.issue_date).toLocaleDateString()}
              {consignment.expected_return_date && ` • Expected return ${new Date(consignment.expected_return_date).toLocaleDateString()}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <PrinterIcon className="w-4 h-4" />
            Print
          </button>
          {isActive && (
            <>
              <button
                onClick={openReturnModal}
                className="inline-flex items-center gap-2 px-4 py-2 border border-orange-300 text-orange-700 rounded-lg hover:bg-orange-50"
              >
                <ArrowUturnLeftIcon className="w-4 h-4" />
                Partial Return
              </button>
              <button
                onClick={openRecallModal}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                <ArrowPathIcon className="w-4 h-4" />
                Recall Stock
              </button>
            </>
          )}
        </div>
      </div>

      {/* Status & Progress */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Status</p>
          <span className={`inline-flex px-3 py-1 mt-2 text-sm font-medium rounded-full ${getStatusColor(consignment.status)}`}>
            {consignment.status.replace('_', ' ')}
          </span>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Progress</p>
          <div className="mt-2">
            <div className="flex justify-between text-sm mb-1">
              <span>{progress}%</span>
              <span className="text-gray-500">{totalSold}/{totalIssued} sold</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-primary-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Expected Return</p>
          <p className="text-lg font-semibold mt-1">
            {consignment.expected_return_date ? new Date(consignment.expected_return_date).toLocaleDateString() : 'Not set'}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Remaining</p>
          <p className="text-lg font-semibold mt-1 text-primary-600">{totalRemaining} units</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Items Table */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Consignment Items</h2>
              {isActive && <span className="text-sm text-gray-500">Click &quot;Record Sale&quot; to update</span>}
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Issued</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Sold</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Returned</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Remaining</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {consignment.consignment_items?.map((item: any) => {
                    const remaining = item.quantity_issued - item.quantity_sold - item.quantity_returned
                    const issuedSerials = getIssuedSerials(item)
                    return (
                      <tr key={item.item_id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                              {item.products?.product_images?.[0] ? (
                                <img src={item.products.product_images[0].image_url} alt="" className="w-full h-full object-cover rounded-lg" />
                              ) : (
                                <CubeIcon className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{item.products?.product_name}</p>
                              <p className="text-sm text-gray-500">{item.products?.sku}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-right">{item.quantity_issued}</td>
                        <td className="px-4 py-4 text-right text-green-600 font-medium">{item.quantity_sold}</td>
                        <td className="px-4 py-4 text-right">{item.quantity_returned}</td>
                        <td className="px-4 py-4 text-right font-bold">{remaining}</td>
                        <td className="px-4 py-4 text-center">
                          {issuedSerials.length > 0 && isActive && (
                            <button
                              onClick={() => openSaleModal(item)}
                              className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm font-medium"
                            >
                              <CurrencyDollarIcon className="w-4 h-4" />
                              Record Sale
                            </button>
                          )}
                          {remaining === 0 && <span className="text-xs text-gray-400">Settled</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Partner Info */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Partner</h2>
            {consignment.customers ? (
              <div className="space-y-1">
                <p className="font-medium text-gray-900">
                  {consignment.customers.company_name || `${consignment.customers.first_name} ${consignment.customers.last_name}`}
                </p>
                {consignment.customers.email && <p className="text-sm text-gray-500">{consignment.customers.email}</p>}
                {consignment.customers.phone && <p className="text-sm text-gray-500">{consignment.customers.phone}</p>}
              </div>
            ) : (
              <p className="text-gray-500">Unknown Partner</p>
            )}
          </div>

          {/* Summary */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Summary</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Issued</span>
                <span className="font-medium">{totalIssued} units</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Sold</span>
                <span className="font-medium text-green-600">{totalSold} units</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Returned</span>
                <span className="font-medium">{totalReturned} units</span>
              </div>
              <div className="border-t pt-3">
                <div className="flex justify-between font-bold">
                  <span>Remaining</span>
                  <span className="text-primary-600">{totalRemaining} units</span>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {consignment.notes && (
            <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
              <h2 className="font-semibold text-gray-900 mb-2">Notes</h2>
              <p className="text-sm text-gray-600">{consignment.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Record Sale Modal */}
      {showSaleModal && selectedItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Record Sale</h3>
            <p className="text-sm text-gray-500 mb-4">
              {selectedItem.products?.product_name} — Select issued serials that were sold
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Sale Price per Unit (KSh)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={salePrice}
                onChange={(e) => setSalePrice(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="0.00"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Serials Sold
                <span className="ml-2 text-xs text-gray-500 font-normal">({saleSerialIds.size} selected)</span>
              </label>
              <div className="border border-gray-200 rounded-lg max-h-60 overflow-auto">
                <div className="grid grid-cols-2 gap-2 p-3">
                  {getIssuedSerials(selectedItem).map((serial: any) => (
                    <label
                      key={serial.serial_id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
                        saleSerialIds.has(serial.serial_id)
                          ? 'bg-primary-50 border-primary-500 text-primary-700'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                        checked={saleSerialIds.has(serial.serial_id)}
                        onChange={() => {
                          setSaleSerialIds((prev) => {
                            const next = new Set(prev)
                            if (next.has(serial.serial_id)) next.delete(serial.serial_id)
                            else next.add(serial.serial_id)
                            return next
                          })
                        }}
                      />
                      <span className="font-mono text-xs truncate">{serial.serial_numbers?.serial_number || `#${serial.serial_id}`}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={closeSaleModal} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleRecordSale}
                disabled={saleMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                <CheckCircleIcon className="w-4 h-4" />
                {saleMutation.isPending ? 'Recording...' : 'Record Sale'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recall Modal */}
      {showRecallModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Recall Stock</h3>
            <p className="text-sm text-gray-500 mb-4">
              Select items to recall. All remaining issued serials for selected items will be returned to stock.
            </p>

            <div className="mb-4 space-y-2">
              {consignment.consignment_items?.map((item: any) => {
                const remaining = item.quantity_issued - item.quantity_sold - item.quantity_returned
                if (remaining <= 0) return null
                return (
                  <label
                    key={item.item_id}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-colors ${
                      recallItemIds.has(item.item_id)
                        ? 'bg-purple-50 border-purple-500'
                        : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                      checked={recallItemIds.has(item.item_id)}
                      onChange={() => {
                        setRecallItemIds((prev) => {
                          const next = new Set(prev)
                          if (next.has(item.item_id)) next.delete(item.item_id)
                          else next.add(item.item_id)
                          return next
                        })
                      }}
                    />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{item.products?.product_name}</p>
                      <p className="text-xs text-gray-500">{remaining} remaining</p>
                    </div>
                  </label>
                )
              })}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
              <textarea
                rows={2}
                value={recallReason}
                onChange={(e) => setRecallReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="Reason for recall..."
              />
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={closeRecallModal} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleRecall}
                disabled={recallMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                <ArrowPathIcon className="w-4 h-4" />
                {recallMutation.isPending ? 'Recalling...' : 'Recall Selected'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Partial Return Modal */}
      {showReturnModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Partial Return</h3>
            <p className="text-sm text-gray-500 mb-4">
              Select specific serial numbers to return to stock.
            </p>

            <div className="mb-4 space-y-3 max-h-72 overflow-auto">
              {consignment.consignment_items?.map((item: any) => {
                const issuedSerials = getIssuedSerials(item)
                if (issuedSerials.length === 0) return null
                return (
                  <div key={item.item_id}>
                    <p className="text-sm font-medium text-gray-900 mb-1">{item.products?.product_name}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {issuedSerials.map((serial: any) => (
                        <label
                          key={serial.serial_id}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
                            returnSerialIds.has(serial.serial_id)
                              ? 'bg-orange-50 border-orange-500 text-orange-700'
                              : 'bg-white border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="w-4 h-4 text-orange-600 rounded border-gray-300 focus:ring-orange-500"
                            checked={returnSerialIds.has(serial.serial_id)}
                            onChange={() => {
                              setReturnSerialIds((prev) => {
                                const next = new Set(prev)
                                if (next.has(serial.serial_id)) next.delete(serial.serial_id)
                                else next.add(serial.serial_id)
                                return next
                              })
                            }}
                          />
                          <span className="font-mono text-xs truncate">{serial.serial_numbers?.serial_number || `#${serial.serial_id}`}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
              <textarea
                rows={2}
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                placeholder="Reason for return..."
              />
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={closeReturnModal} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handlePartialReturn}
                disabled={returnMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                <ArrowUturnLeftIcon className="w-4 h-4" />
                {returnMutation.isPending ? 'Returning...' : `Return ${returnSerialIds.size} Item${returnSerialIds.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
